import {
    AppState,
    AuthenticationState,
    BreachContext,
    BreachScreenContext,
    THEWEST_ALL,
} from '@news-mono/web-common'
import {
    GetOffersResult,
    OfferVariantType,
} from '@west-australian-newspapers/subscription-offer-types'
import { consoleLogger } from '@west-australian-newspapers/typescript-log'
import { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
    OfferGroupType,
    transformOffersResultToPackageOption,
} from '../../subscription-offers/SubscriptionOffers'
import {
    BASE_50_DEAL_OFFERCODE,
    EVERYDAY_DIGITAL,
    EVERYDAY_DIGITAL_52_WEEKS_RATE_CODE,
    EVERYDAY_DIGITAL_INCREASE,
    FIFTY_OFF_EVERYDAY_DIGITAL,
    FIFTY_OFF_EVERYDAY_DIGITAL_12_WEEKS_RATE_CODE,
    FIFTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
    FIFTY_OFF_FRI_MON_PAPER_AND_DIGITAL_12_WEEKS_RATE_CODE,
    FIFTY_OFF_FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL,
    FIFTY_OFF_WE_PAPERS_AND_EVERYDAY_DIGITAL,
    FIFTY_OFF_WE_PAPER_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
    FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL,
    WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
    WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL_INCREASE,
    WEEKLY_DIGITAL_RATE_CODE,
    WEEKLY_DIGITAL_RATE_CODE_INCREASE,
    WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
    WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE_INCREASE,
} from './constants'
import { SubscriptionPackageOptions } from './helpers/BreachWallTypes'

export type SubscriptionPackages = SubscriptionPackageOptions[]

// Re-export constants for backward compatibility
export {
    BASE_50_DEAL_OFFERCODE,
    EVERYDAY_DIGITAL,
    EVERYDAY_DIGITAL_52_WEEKS_RATE_CODE,
    EVERYDAY_DIGITAL_INCREASE,
    FIFTY_OFF_EVERYDAY_DIGITAL,
    FIFTY_OFF_EVERYDAY_DIGITAL_12_WEEKS_RATE_CODE,
    FIFTY_OFF_FRI_MON_PAPER_AND_DIGITAL_12_WEEKS_RATE_CODE,
    FIFTY_OFF_WE_PAPERS_AND_EVERYDAY_DIGITAL,
    FIFTY_OFF_WE_PAPER_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
    FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL,
    WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
    WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL_INCREASE,
    WEEKLY_DIGITAL_RATE_CODE,
    WEEKLY_DIGITAL_RATE_CODE_INCREASE,
    WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
    WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE_INCREASE,
}

/**
 * Retrieves subscription packages for a given breach wall content type.
 *
 * @param {OfferGroupType} offerGroup - The offer group to fetch from
 * @param {OfferVariantType} type - The type of breach wall content to retrieve packages for.
 * @returns {SubscriptionPackages} An array of subscription packages corresponding to the given type.
 *
 */
export const getOffersApiPackageDetails = (
    offerGroup: OfferGroupType,
    type: OfferVariantType,
    apiOffers?: GetOffersResult,
): SubscriptionPackages => {
    const log = consoleLogger()

    const offerPackage = transformOffersResultToPackageOption(
        offerGroup,
        apiOffers,
    )[type]
    if (!offerPackage) {
        log.error(`No API packages found for breach wall content type: ${type}`)
        return []
    }

    return offerPackage
}

export function getWeeklyPrice(rateCode: string): number {
    switch (rateCode) {
        case WEEKLY_DIGITAL_RATE_CODE:
            return EVERYDAY_DIGITAL
        case WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE:
            return WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL
        case FIFTY_OFF_EVERYDAY_DIGITAL_12_WEEKS_RATE_CODE:
            return FIFTY_OFF_EVERYDAY_DIGITAL
        case FIFTY_OFF_FRI_MON_PAPER_AND_DIGITAL_12_WEEKS_RATE_CODE:
            return FIFTY_OFF_FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL
        case FIFTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE:
            return FIFTY_OFF_EVERYDAY_DIGITAL
        default:
            return 0
    }
}

export function useUpdateBreachWallContext({
    publication,
    breachCreativeName,
    breachDeal,
    leadDepth,
    offerCode,
    packagePath,
    requiredAccessLevel,
}: BreachContext) {
    const breachScreenContext = useContext(BreachScreenContext)

    const authentication = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )

    const { entitlements, isLoggedIn } = authentication

    // a returning user is a user who has registered, logged in, and has not completed a purchase
    const returningUser = isLoggedIn && !entitlements?.includes(THEWEST_ALL)

    useEffect(() => {
        breachScreenContext.setContext({
            publication,
            returningUser,
            breachCreativeName,
            breachDeal,
            leadDepth,
            offerCode,
            packagePath,
            requiredAccessLevel,
        })
        return () => {
            breachScreenContext.clearContext()
        }

        // We can ignore the breachScreenContext dependency because the setContext and clearContext functions aren't going to change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        returningUser,
        publication,
        breachCreativeName,
        breachDeal,
        leadDepth,
        offerCode,
        packagePath,
        requiredAccessLevel,
    ])
}
